import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Upload,
  Space,
  Typography,
  message,
  ConfigProvider,
  Popconfirm,
  TimePicker,
} from "antd";
import FormItem from "antd/es/form/FormItem";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { uploadFile } from "../../api/file";
import {
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

function getFormItem(item) {
  switch (item.type) {
    case "text":
      return <Input />;
    case "email":
      return <Input />;
    case "number":
      return (
        <InputNumber
          addonBefore={item.addonBefore}
          addonAfter={item.addonAfter}
          className="w-full"
        />
      );
    case "text-area":
      return <Input.TextArea maxLength={item.maxLength || 200} showCount disabled={item.disabled} />;
    case "password":
      return <Input.Password />;
    case "async-select":
      return (
        <Select
          placeholder={item.placeholder || "Search to Select"}
          onSearch={item.onSearch}
          options={item.options}
          defaultActiveFirstOption={false}
          filterOption={false}
          mode={item.mode || "tag"}
          showSearch
          disabled={item.disabled}
          onSelect={item.onSelect}
        />
      );
    case "avatar":
      return (
        <Upload
          customRequest={async ({ file, onSuccess, onError }) => {
            try {
              const res = await uploadFile(file, item.fields);
              onSuccess(res);
            } catch (err) {
              onError(err);
            }
          }}
          listType="picture-card"
          defaultFileList={item.fileList}
          className="upload-list-inline"
          multiple={false}
          maxCount={item.maxCount || 1}
        >
          Upload
        </Upload>
      );
    case "attachments":
      return (
        <Upload
          customRequest={async ({ file, onSuccess, onError }) => {
            try {
              const res = await uploadFile(file, item.fields);
              onSuccess(res);
            } catch (err) {
              onError(err);
            }
          }}
          listType={item.listType || "picture"}
          defaultFileList={item.fileList}
          className="upload-list-inline"
        >
          <Space direction="vertical">
            <Typography.Text type="secondary">
              {item.additionalInfo}
            </Typography.Text>
            {!item.listType ? (
              <Button icon={<UploadOutlined />}>Upload</Button>
            ) : (
              <UploadOutlined />
            )}
          </Space>
        </Upload>
      );
    case "date":
      return <DatePicker showTime={item.showTime} format={item.format} className="w-full" />;

    case "time":
      return <TimePicker format={item.format} defaultValue={dayjs("00:00:00", item.format)} className="w-full" />;
    default:
      return null;
  }
}

export function EntitySection({ items }) {
  return (
    <>
      <Row gutter={8}>
        {items.map((item, i) => {
          const rules = [
            {
              required: item.required,
              message: `${item.label} is required`,
            },
          ];
          if (item.type === "email") {
            rules.push({
              type: "email",
              message: "The input is not valid E-mail!",
            });
          }
          return (
            <Col span={item.col || 8} key={`${i}`}>
              <FormItem name={item.name} label={item.label} rules={rules}>
                {getFormItem(item)}
              </FormItem>
            </Col>
          );
        })}
      </Row>
    </>
  );
}

export function EntityForm({
  sections,
  initialValues,
  title,
  onCancel,
  onSubmit,
  onDelete,
  open,
  isEditMode = false,
  children
}) {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [mode, setMode] = useState("view"); // edit |  view

  useEffect(() => {
    if (isEditMode) {
      setMode("view");
      return;
    }
    setMode("edit");
  }, [isEditMode]);

  return (
    <Modal
      title={title}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            setConfirmLoading(true);
            return onSubmit(values);
          })
          .then(() => form.resetFields())
          .then(() => setConfirmLoading(false))
          .catch((info) => {
            if (info.errorFields) {
              console.log("Validation failed:", info);
            } else {
              message.error("Something went wrong!");
            }
            setConfirmLoading(false);
          });
      }}
      onCancel={onCancel}
      okText="Submit"
      okButtonProps={{ size: "large" }}
      confirmLoading={confirmLoading}
      cancelButtonProps={{ size: "large" }}
      width={"70%"}
      closeIcon={null}
      open={open}
    >
      {isEditMode && (
        <Space
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            margin: "12px 24px",
          }}
          size={0}
        >
          <Button
            size="large"
            type="link"
            icon={mode === "edit" ? null : <EditOutlined />}
            onClick={() => setMode("edit")}
          />
          <Popconfirm
            title="Delete the task"
            description="Are you sure to delete this task?"
            onConfirm={() => onDelete()}
            okText="Yes"
            cancelText="No"
          >
            <Button size="large" type="link" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      )}
      <ConfigProvider
        theme={{
          token: {
            colorTextQuaternary: "black",
            // colorBgContainerDisabled: "transparent",
            colorBorder: mode === "view" ? "transparent" : "#d9d9d9",
          },
        }}
      >
        <Form
          form={form}
          initialValues={initialValues}
          layout="vertical"
          size="large"
          disabled={mode === "view"}
        >
          {sections.map((section, i) => {
            return (
              <EntitySection
                key={`${i}`}
                title={section.title}
                items={section.items}
              />
            );
          })}
        </Form>
        {children}
      </ConfigProvider>
    </Modal>
  );
}
